/**
 * Data and schedules — the pure half (bata-ui#166 W1-1/W1-2, `docs/adr/2006`).
 *
 *   GET /api/v1/foundations/schedules?country_key=   every schedule the orchestrator last reported
 *   GET /api/v1/foundations/feeds?country_key=        every landed feed of the market, and its contract
 *
 * Everything the screen states is decided here, on the platform's values, and tested:
 *
 * - **A stopped or unknown schedule never reads as running.** Only the orchestrator's own word
 *   `RUNNING`, on a report fresh enough to still be true, with a next tick still ahead, yields a
 *   next-run date — the rule the Cockpit's cycle band applies (ADR 9016), restated here because a
 *   feature may not import the shell. Everything else is "stopped" or "state unknown since …",
 *   the orchestrator's last word beside it.
 * - **A run outcome is the orchestrator's word, verbatim** (`landed`, `unchanged`, `failed`,
 *   `skipped`) — `unchanged` is a successful publish that found nothing new (bata-data ADR 2009),
 *   never a failure and never "landed".
 * - **Freshness is judged only against the contract's own threshold** (`freshnessState`,
 *   bata-platform ADR 1003): `undeclared` is "no threshold declared", never fresh.
 * - **Double counting is a warning with its count**: an accumulating feed whose in-force partition
 *   holds more than one unsuperseded batch sums them all (W0-3b).
 */

import type { FeedStatus, ScheduleStatus } from '@/api/generated';

/** How old a schedule report may be and still be read as the schedule's state (ADR 9016's value). */
export const SCHEDULE_REPORT_STALE_MS = 10 * 60 * 1000;

/** `?country=` — the market the screen reads. No default: the URL states it or the planner picks it. */
export const COUNTRY_PARAM = 'country';

const COUNTRY = /^[A-Z]{2}$/;

/** The market a search string states, or `null`. Pure. */
export function readCountry(search: string): string | null {
  const raw = new URLSearchParams(search).get(COUNTRY_PARAM)?.trim().toUpperCase() ?? '';
  return COUNTRY.test(raw) ? raw : null;
}

/** The markets a picker may offer: those the reads name, sorted, the linked one always included. */
export function countryChoices(named: readonly (string | null | undefined)[], linked: string | null): readonly string[] {
  const set = new Set<string>();
  for (const value of named) if (typeof value === 'string' && COUNTRY.test(value)) set.add(value);
  if (linked !== null) set.add(linked);
  return [...set].sort();
}

// ── schedules ────────────────────────────────────────────────────────────────

export type ScheduleNext =
  | { readonly kind: 'scheduled'; readonly at: string }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'unknownSince'; readonly reportedAt: string; readonly status: string };

const instant = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
};

export function scheduleNext(row: Pick<ScheduleStatus, 'status' | 'reportedAt' | 'nextTickAt'>, now: number): ScheduleNext {
  const unknown: ScheduleNext = { kind: 'unknownSince', reportedAt: row.reportedAt, status: row.status };
  const reported = instant(row.reportedAt);
  if (reported === null || now - reported > SCHEDULE_REPORT_STALE_MS) return unknown;
  if (row.status === 'STOPPED') return { kind: 'stopped' };
  if (row.status !== 'RUNNING') return unknown;
  const next = instant(row.nextTickAt);
  if (next === null || row.nextTickAt === null || next <= now) return unknown;
  return { kind: 'scheduled', at: row.nextTickAt };
}

/** The decision cycle a schedule drives, in words; `null` = it drives none (a publish, a refit …). */
export const CYCLE_KEY: Readonly<Record<string, string>> = {
  inseason_replenishment: 'foundations.schedules.cycle.inseasonReplenishment',
  inseason_price: 'foundations.schedules.cycle.inseasonPrice',
  exit: 'foundations.schedules.cycle.exit',
};

export function cycleKey(cycle: string | null): string {
  if (cycle === null) return 'foundations.schedules.cycle.none';
  return CYCLE_KEY[cycle] ?? 'foundations.schedules.cycle.other';
}

/** The orchestrator's run outcome words (bata-data `bata/outcome`), each spelled out. */
export const OUTCOME_KEY: Readonly<Record<string, string>> = {
  landed: 'foundations.schedules.outcome.landed',
  unchanged: 'foundations.schedules.outcome.unchanged',
  failed: 'foundations.schedules.outcome.failed',
  skipped: 'foundations.schedules.outcome.skipped',
};

export type RunOutcome =
  | { readonly kind: 'none' }
  | { readonly kind: 'outcome'; readonly key: string; readonly word: string; readonly failed: boolean }
  /** No outcome reported, only the run's status (verbatim). */
  | { readonly kind: 'status'; readonly status: string; readonly failed: boolean };

const FAILED_STATUSES: ReadonlySet<string> = new Set(['FAILURE', 'CANCELED']);

export function runOutcome(row: Pick<ScheduleStatus, 'lastRun'>): RunOutcome {
  const run = row.lastRun;
  if (run === null) return { kind: 'none' };
  if (run.outcome !== null && run.outcome !== '') {
    return {
      kind: 'outcome',
      key: OUTCOME_KEY[run.outcome] ?? 'foundations.schedules.outcome.other',
      word: run.outcome,
      failed: run.outcome === 'failed' || FAILED_STATUSES.has(run.status ?? ''),
    };
  }
  if (run.status !== null && run.status !== '') return { kind: 'status', status: run.status, failed: FAILED_STATUSES.has(run.status) };
  return { kind: 'none' };
}

/** The tick states a cycle schedule's newest tick can have, for the column's words. */
export const TICK_STATE_KEY: Readonly<Record<string, string>> = {
  succeeded: 'foundations.schedules.tick.succeeded',
  failed: 'foundations.schedules.tick.failed',
  in_progress: 'foundations.schedules.tick.inProgress',
  refused: 'foundations.schedules.tick.refused',
  produced_nothing: 'foundations.schedules.tick.producedNothing',
  incomplete: 'foundations.schedules.tick.incomplete',
  skipped: 'foundations.schedules.tick.skipped',
};

export function tickStateKey(state: string | null): string {
  if (state === null) return 'foundations.schedules.tick.none';
  return TICK_STATE_KEY[state] ?? 'foundations.schedules.tick.other';
}

// ── feeds ────────────────────────────────────────────────────────────────────

/** A feed row's identity since W1-1: one row per (landing feed, market). */
export function feedRowKey(feed: Pick<FeedStatus, 'feedId' | 'countryKey'>): string {
  return `${feed.feedId}\u0000${feed.countryKey ?? ''}`;
}

export const FRESHNESS_KEY: Readonly<Record<string, string>> = {
  fresh: 'foundations.feeds.freshness.fresh',
  stale: 'foundations.feeds.freshness.stale',
  no_batch: 'foundations.feeds.freshness.noBatch',
  undeclared: 'foundations.feeds.freshness.undeclared',
};

/** The freshness verdict's words; a platform before v0.51 publishes none (`notPublished`). */
export function freshnessKey(state: string | undefined): string {
  if (state === undefined) return 'foundations.feeds.freshness.notPublished';
  return FRESHNESS_KEY[state] ?? 'foundations.feeds.freshness.other';
}

/** Whether the row is double counted, as the platform judged it (accumulating and >1 unsuperseded). */
export function isDoubleCounted(feed: Pick<FeedStatus, 'doubleCounted'>): boolean {
  return feed.doubleCounted === true;
}

/** The contract's freshness threshold in hours, or `null` when none is declared. */
export function freshnessThreshold(feed: Pick<FeedStatus, 'contract'>): number | null {
  const hours = feed.contract?.freshnessErrorAfterHours;
  return typeof hours === 'number' ? hours : null;
}
